import React from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import { useAuth } from '../../contexts/AuthContext';
import { track } from '../../services/api';
import PackageSwitcher from './PackageSwitcher';
import './PackageBar.css';

/**
 * The app bar for a package.
 *
 * Before this the package pages had no shell at all: no Lomi mark, no account,
 * and a text breadcrumb — "Packages / UAT — AAU Entrance" — standing in for
 * navigation that did not exist. A student inside a package could not tell
 * they were still in Lomi, could not reach their profile, and above all could
 * not move between packages, which is the one thing the package system exists
 * to make possible.
 *
 * Three jobs, in the order a student needs them:
 *
 *   the mark      you are in Lomi
 *   the switcher  WHICH exam you are looking at, and how to change it
 *   the account   you, and the way out
 *
 * The switcher is the load-bearing part. A student can hold several packages,
 * so "which one am I in" is a first-class question, not a breadcrumb. It lists
 * what they hold and offers the catalog when they want another.
 *
 * Mobile-first: one 52px row, the mark shrinks to the lemon, and the package
 * name truncates rather than wrapping. It replaces the breadcrumb row rather
 * than adding to it, so the chrome budget goes down, not up.
 */

const PackageBar: React.FC<{ pkg: string; pkgName: string }> = ({ pkg, pkgName }) => {
  const navigate = useNavigate();
  const { student } = useAuth();

  const go = (to: string, how: string) => {
    track('package.bar.navigate', { package: pkg, to: how });
    navigate(to);
  };

  const initial =
    (student?.display_name || student?.first_name || 'S').trim().charAt(0).toUpperCase();

  return (
    <header className="pb-bar">
      <button
        className="pb-mark"
        onClick={() => go('/', 'home')}
        aria-label="Lomi home"
      >
        <Logo height={26} />
      </button>

      <PackageSwitcher current={pkg} currentName={pkgName} />

      <button
        className="pb-account"
        onClick={() => go('/profile', 'profile')}
        aria-label="Your account"
        title={student?.display_name || 'Account'}
      >
        {student?.avatar_url ? (
          <img src={student.avatar_url} alt="" />
        ) : (
          <span>{initial}</span>
        )}
      </button>
    </header>
  );
};

export default PackageBar;
